import {
  COMPRESSOR_PRESET,
  DEFAULT_EQ_CURVE,
  HPF_PRESET,
  type EqCurve,
} from './presets';
import {
  clampMasterGainDb,
  dbToLinear,
  LIMITER_LOOKAHEAD_MS,
  PEAK_CEILING_DBFS,
} from './safety';

// Respect a non-root base path (e.g. GitHub Pages project sites).
const BASE = import.meta.env.BASE_URL ?? '/';
const LIMITER_WORKLET_URL = `${BASE}worklets/brickwall-limiter.js`.replace('//worklets', '/worklets');
const LIMITER_PROCESSOR_NAME = 'brickwall-limiter';

export interface AudioGraph {
  source: MediaStreamAudioSourceNode;
  hpf: BiquadFilterNode;
  eq: BiquadFilterNode[];
  compressor: DynamicsCompressorNode;
  makeupGain: GainNode;
  masterGain: GainNode;
  limiter: AudioWorkletNode;
  destination: MediaStreamAudioDestinationNode;
}

export interface BuildAudioGraphParams {
  audioContext: AudioContext;
  source: MediaStreamAudioSourceNode;
  initialGainDb: number;
  eqCurve?: EqCurve;
  onMeter?: (rmsDb: number) => void;
}

export async function buildAudioGraph(params: BuildAudioGraphParams): Promise<AudioGraph> {
  const { audioContext, source, initialGainDb, onMeter } = params;
  const eqCurve = params.eqCurve ?? DEFAULT_EQ_CURVE;

  // 1. High-pass: remove rumble, HVAC, handling.
  const hpf = audioContext.createBiquadFilter();
  hpf.type = 'highpass';
  hpf.frequency.value = HPF_PRESET.frequency;
  hpf.Q.value = HPF_PRESET.Q;

  // 2. Presbycusis EQ (5 biquads in series).
  const eq = eqCurve.map((band) => {
    const node = audioContext.createBiquadFilter();
    node.type = band.type;
    node.frequency.value = band.frequency;
    node.Q.value = band.Q;
    node.gain.value = band.gainDb;
    return node;
  });

  // 3. WDRC compressor.
  const compressor = audioContext.createDynamicsCompressor();
  compressor.threshold.value = COMPRESSOR_PRESET.thresholdDb;
  compressor.ratio.value = COMPRESSOR_PRESET.ratio;
  compressor.knee.value = COMPRESSOR_PRESET.kneeDb;
  compressor.attack.value = COMPRESSOR_PRESET.attackSec;
  compressor.release.value = COMPRESSOR_PRESET.releaseSec;

  // 4. Makeup gain (compensate compressor level drop, +6 dB fixed).
  const makeupGain = audioContext.createGain();
  makeupGain.gain.value = dbToLinear(COMPRESSOR_PRESET.makeupGainDb);

  // 5. Master gain (user controlled). Re-clamp at the audio layer as
  // defence-in-depth — the store already clamps but this stops a future
  // caller from ever pushing the GainNode past the safety ceiling.
  const masterGain = audioContext.createGain();
  masterGain.gain.value = dbToLinear(clampMasterGainDb(initialGainDb));

  // 6. Brickwall limiter (AudioWorklet).
  await audioContext.audioWorklet.addModule(LIMITER_WORKLET_URL);
  const limiter = new AudioWorkletNode(audioContext, LIMITER_PROCESSOR_NAME, {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [1],
    processorOptions: {
      sampleRate: audioContext.sampleRate,
      ceilingDb: PEAK_CEILING_DBFS,
      lookaheadMs: LIMITER_LOOKAHEAD_MS,
    },
  });
  if (onMeter) {
    limiter.port.onmessage = (ev) => {
      const data = ev.data as { type?: string; rmsDb?: number };
      if (data?.type === 'meter' && typeof data.rmsDb === 'number') {
        onMeter(data.rmsDb);
      }
    };
  }

  // 7. Destination (a MediaStream for the <audio> element).
  const destination = audioContext.createMediaStreamDestination();

  // Wire: source -> hpf -> eq[] -> compressor -> makeup -> master -> limiter -> destination
  source.connect(hpf);
  let prev: AudioNode = hpf;
  for (const band of eq) {
    prev.connect(band);
    prev = band;
  }
  prev.connect(compressor);
  compressor.connect(makeupGain);
  makeupGain.connect(masterGain);
  masterGain.connect(limiter);
  limiter.connect(destination);

  return { source, hpf, eq, compressor, makeupGain, masterGain, limiter, destination };
}

export function disconnectGraph(graph: AudioGraph): void {
  const nodes: AudioNode[] = [
    graph.source,
    graph.hpf,
    ...graph.eq,
    graph.compressor,
    graph.makeupGain,
    graph.masterGain,
    graph.limiter,
    graph.destination,
  ];
  for (const node of nodes) {
    try {
      node.disconnect();
    } catch {
      // ignore
    }
  }
  try {
    graph.limiter.port.onmessage = null;
  } catch {
    // ignore
  }
}
